import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RecordNav } from "../components/RecordNav";

const itemKey = (i) => i.sku || i.name;
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FloppyDisk, CaretDown, CaretRight, Trash, Sparkle, Note as NoteIcon, ListChecks, Plus, Paperclip,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAutoSave, AutoSaveIndicator } from "../lib/useAutoSave";
import { StatusChip, ConfirmDialog } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AttachmentsPane } from "../components/AttachmentsPane";
import { usePerms } from "../lib/perms";
import { VendorPricesEditor } from "../components/VendorPrices";
import { ItemHistory } from "../components/ItemHistory";
import { ViewOnlyBadge } from "../components/Permission";
import { isMobileViewport } from "../components/LineItemText";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function Section({ title, children, defaultOpen = true, mobileCollapsed = false, wide = false }) {
  const [open, setOpen] = useState(defaultOpen && !(mobileCollapsed && isMobileViewport()));
  const key = title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="border border-border bg-card mb-4" data-testid={`isection-${key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`isection-toggle-${key}`}>
        {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
        <span className="font-bold text-sm tracking-tight">{title}</span>
      </button>
      {open && <div className={`px-4 pb-4 pt-1 grid ${wide ? "grid-cols-1" : "grid-cols-2"} md:grid-cols-2 gap-3 md:gap-4`}>{children}</div>}
    </div>
  );
}
function F({ label, children }) {
  return (<div><Label className="overline-label">{label}</Label><div className="mt-1">{children}</div></div>);
}

export default function ItemCard() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: item, isLoading } = useQuery({ queryKey: ["item", id], queryFn: async () => (await api.get(`/items/${id}`)).data, enabled: !isNew });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });

  const [form, setForm] = useState({
    name: "", sku: "", description: "", category: "General", unit_of_measure: "PCS",
    unit_price: 0, cost: 0, quantity: 0, reorder_point: 0, vendor_id: "", location_code: "",
    lead_time_days: 0, vendor_part_no: "", quote_ref_no: "", vendor_prices: [],
  });
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (item && !dirtyRef.current) {
      setForm((f) => ({ ...f, ...item, vendor_prices: [...(item.vendor_prices || []), ...(f.vendor_prices || []).filter((p) => !p.vendor_id)] }));
    }
  }, [item]); // eslint-disable-line react-hooks/exhaustive-deps
  const setF = (k, v) => { dirtyRef.current = true; setForm((f) => ({ ...f, [k]: v })); };

  const profit = Number(form.unit_price) > 0 ? Math.round((Number(form.unit_price) - Number(form.cost)) / Number(form.unit_price) * 1000) / 10 : 0;
  const low = Number(form.quantity) <= Number(form.reorder_point);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["items"] }); qc.invalidateQueries({ queryKey: ["item", id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const payload = () => ({
    name: form.name, sku: form.sku, description: form.description, category: form.category, unit_of_measure: form.unit_of_measure,
    unit_price: Number(form.unit_price), cost: Number(form.cost), quantity: Number(form.quantity), reorder_point: Number(form.reorder_point),
    vendor_id: form.vendor_id, location_code: form.location_code,
    lead_time_days: Number(form.lead_time_days || 0), vendor_part_no: form.vendor_part_no, quote_ref_no: form.quote_ref_no,
    vendor_prices: (form.vendor_prices || []).filter((p) => p.vendor_id).map((p) => ({ vendor_id: p.vendor_id, currency: p.currency || "", cost: Number(p.cost || 0) })),
  });
  const save = useMutation({
    mutationFn: async () => isNew ? (await api.post("/items", payload())).data : (await api.put(`/items/${id}`, payload())).data,
    onSuccess: (res) => { invalidate(); toast.success(isNew ? "Item created" : "Item saved"); if (isNew) navigate(`/inventory/item/${res.id}`, { replace: true }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const createdRef = useRef(false);
  const autoSaveFn = async () => {
    if (isNew) {
      if (createdRef.current) return;
      createdRef.current = true;
      try {
        const res = (await api.post("/items", payload())).data;
        invalidate();
        navigate(`/inventory/item/${res.id}`, { replace: true });
        return res;
      } catch (e) { createdRef.current = false; throw e; }
    }
    dirtyRef.current = false;
    const res = (await api.put(`/items/${id}`, payload())).data;
    invalidate();
    return res;
  };
  const { can } = usePerms();
  const viewOnly = !can("items.edit");
  const { status: autoStatus, flush: autoSaveFlush } = useAutoSave({ active: !viewOnly, canSave: !!form.name, createMode: isNew, snapshot: payload(), save: autoSaveFn });
  const [delOpen, setDelOpen] = useState(false);
  const del = useMutation({ mutationFn: async () => api.delete(`/items/${id}`), onSuccess: () => { invalidate(); toast.success("Deleted"); navigate("/inventory"); } });

  if (!isNew && isLoading) return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-up pb-10" data-testid="item-card" onBlur={autoSaveFlush}>
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => navigate("/inventory")} className="p-1.5 -ml-1.5 text-muted-foreground hover:text-primary" data-testid="item-card-back"><ArrowLeft size={18} weight="bold" /></button>
      </div>
      <div className="flex items-center gap-3 mb-4">
        {!isNew && item?.primary_image_id && <ItemHeaderImage fileId={item.primary_image_id} />}
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="item-card-title">{isNew ? "New Item" : item.name}</h1>
        <StatusChip status={low ? "Low" : "Open"} />
        <ViewOnlyBadge show={viewOnly && !isNew} />
        {!isNew && <div className="ml-auto"><RecordNav source="items" currentId={id} sortKey={itemKey} basePath="/inventory/item" testId="item-nav" /></div>}
      </div>

      <div className="flex items-center gap-1 flex-wrap border border-border bg-card px-2 py-1.5 mb-5 rounded-sm">
        {!viewOnly && <button onMouseDown={(e) => e.preventDefault()} onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="item-ribbon-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"><FloppyDisk size={15} weight="bold" /> {isNew ? "Create" : "Save"}</button>}
        {!viewOnly && <AutoSaveIndicator status={autoStatus} isNew={isNew} testid="item-autosave" />}
        {!isNew && !viewOnly && <button onClick={() => setDelOpen(true)} data-testid="item-ribbon-delete" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-foreground hover:bg-secondary"><Trash size={15} weight="bold" /> Delete</button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <fieldset disabled={viewOnly} className="lg:col-span-2 min-w-0" data-testid="item-form-fields">
          <Section title="Item">
            <F label="No."><Input data-testid="item-name" value={form.name} onChange={(e) => setF("name", e.target.value)} className="rounded-sm" /></F>
            <F label="Description"><Input value={form.description} onChange={(e) => setF("description", e.target.value)} className="rounded-sm" /></F>
            <F label="Category"><Input value={form.category} onChange={(e) => setF("category", e.target.value)} className="rounded-sm" /></F>
            <F label="Base Unit of Measure"><Input value={form.unit_of_measure} onChange={(e) => setF("unit_of_measure", e.target.value)} className="rounded-sm" /></F>
          </Section>

          <Section title="Inventory">
            <F label="On Hand Quantity"><Input type="number" data-testid="item-qty" value={form.quantity} onChange={(e) => setF("quantity", e.target.value)} className="rounded-sm num" /></F>
            <F label="Reorder Point"><Input type="number" value={form.reorder_point} onChange={(e) => setF("reorder_point", e.target.value)} className="rounded-sm num" /></F>
            <F label="Location Code">
              <Select value={form.location_code || "none"} onValueChange={(v) => setF("location_code", v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="item-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.code}>{l.code} · {l.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </Section>

          <Section title="Costs & Posting">
            <F label="Unit Cost"><Input type="number" step="0.01" data-testid="item-cost" value={form.cost} onChange={(e) => setF("cost", e.target.value)} className="rounded-sm num" /></F>
            <F label="Inventory Value"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular">{money(Number(form.quantity) * Number(form.cost))}</div></F>
          </Section>

          <Section title="Prices & Sales">
            <F label="Unit Price"><Input type="number" step="0.01" data-testid="item-price" value={form.unit_price} onChange={(e) => setF("unit_price", e.target.value)} className="rounded-sm num" /></F>
            <F label="Profit %"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="item-profit">{profit}%</div></F>
          </Section>

          <Section title="Replenishment">
            <F label="Vendor No.">
              <Select value={form.vendor_id || "none"} onValueChange={(v) => setF("vendor_id", v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="item-vendor"><SelectValue placeholder="Preferred vendor" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Lead Time (days)"><Input type="number" data-testid="item-lead-time" value={form.lead_time_days} onChange={(e) => setF("lead_time_days", e.target.value)} className="rounded-sm num" /></F>
            <F label="Vendor Part No."><Input data-testid="item-vendor-part-no" value={form.vendor_part_no} onChange={(e) => setF("vendor_part_no", e.target.value)} className="rounded-sm" /></F>
            <F label="Quote Ref No."><Input data-testid="item-quote-ref-no" value={form.quote_ref_no} onChange={(e) => setF("quote_ref_no", e.target.value)} className="rounded-sm" /></F>
          </Section>

          <Section title="Vendor Prices" wide mobileCollapsed>
            <VendorPricesEditor rows={form.vendor_prices || []} onChange={(v) => setF("vendor_prices", v)} vendors={vendors} disabled={viewOnly} />
          </Section>
        </fieldset>

        <div className="lg:col-span-1">
          <div className="border border-border bg-card sticky top-4" data-testid="item-info-pane">
            <Tabs defaultValue="details">
              <TabsList className="w-full rounded-none border-b border-border bg-secondary/40 h-auto p-0">
                <TabsTrigger value="details" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><ListChecks size={14} className="mr-1" /> Details</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="item-pane-tab-notes"><NoteIcon size={14} className="mr-1" /> Notes</TabsTrigger>
                <TabsTrigger value="files" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="item-pane-tab-files"><Paperclip size={14} className="mr-1" /> Files</TabsTrigger>
                <TabsTrigger value="summary" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card"><Sparkle size={14} className="mr-1" /> Summary</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="p-4 space-y-2.5 text-sm mt-0">
                <Row label="No." value={isNew ? "—" : item.name} mono />
                <Row label="Category" value={form.category} />
                <Row label="On Hand" value={`${form.quantity} ${form.unit_of_measure}`} tabular />
                <Row label="Vendor" value={vendors.find((v) => v.id === form.vendor_id)?.name || "—"} />
                <div className="border-t border-border pt-2.5 space-y-2.5">
                  <Row label="Unit Cost" value={money(form.cost)} tabular />
                  <Row label="Unit Price" value={money(form.unit_price)} tabular />
                  <Row label="Profit %" value={`${profit}%`} tabular bold />
                </div>
              </TabsContent>
              <TabsContent value="notes" className="p-4 mt-0">
                <NotesPane itemId={id} isNew={isNew} readOnly={viewOnly} notes={item?.notes || []} onAdded={() => qc.invalidateQueries({ queryKey: ["item", id] })} />
              </TabsContent>
              <TabsContent value="files" className="p-4 mt-0">
                <AttachmentsPane entityType="item" entityId={id} isNew={isNew} readOnly={viewOnly} label="Upload File"
                  allowPrimary primaryId={item?.primary_image_id || null}
                  onPrimaryChange={() => qc.invalidateQueries({ queryKey: ["item", id] })} />
              </TabsContent>
              <TabsContent value="summary" className="p-4 mt-0">
                <SummaryPane itemId={id} isNew={isNew} existing={item?.ai_summary} onDone={() => qc.invalidateQueries({ queryKey: ["item", id] })} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {!isNew && <ItemHistory itemId={id} />}

      <ConfirmDialog open={delOpen} onOpenChange={setDelOpen} title="Delete this item?" description={`${form.name} will be permanently deleted.`} confirmLabel="Delete" testid="item-card-delete-confirm" onConfirm={() => del.mutate()} />
    </div>
  );
}

function Row({ label, value, mono, tabular, bold }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">{label}</span>
      <span className={`${mono ? "font-mono text-xs" : ""} ${tabular ? "tabular" : ""} ${bold ? "font-bold text-primary" : "font-medium"}`}>{value}</span>
    </div>
  );
}

function NotesPane({ itemId, isNew, notes, onAdded, readOnly }) {
  const [text, setText] = useState("");
  const add = useMutation({ mutationFn: async () => (await api.post(`/items/${itemId}/notes`, { text })).data, onSuccess: () => { setText(""); onAdded(); toast.success("Note added"); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  if (isNew) return <p className="text-sm text-muted-foreground">Save the item to add notes.</p>;
  return (
    <div className="space-y-3">
      <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="item-notes-list">
        {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
        {notes.map((n) => <div key={n.id} className="border border-border rounded-sm p-2.5 text-sm"><p className="whitespace-pre-wrap">{n.text}</p><p className="text-[11px] text-muted-foreground mt-1">{n.created_by} · {(n.created_at || "").slice(0, 10)}</p></div>)}
      </div>
      {!readOnly && <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add a note…" data-testid="item-note-input" className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" />}
      {!readOnly && <Button onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid="item-add-note" className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full"><Plus size={14} className="mr-1" /> Add Note</Button>}
    </div>
  );
}

function SummaryPane({ itemId, isNew, existing, onDone }) {
  const [summary, setSummary] = useState(existing || "");
  useEffect(() => { setSummary(existing || ""); }, [existing]);
  const gen = useMutation({ mutationFn: async () => (await api.post(`/ai/summarize-item/${itemId}`)).data, onSuccess: (r) => { setSummary(r.summary); onDone(); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate summary") });
  if (isNew) return <p className="text-sm text-muted-foreground">Save the item to generate an Atlas summary.</p>;
  return (
    <div className="space-y-3">
      {summary ? <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="item-ai-summary">{summary}</p> : <p className="text-sm text-muted-foreground">Let Atlas summarize this item.</p>}
      <Button onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="item-generate-summary" className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full">
        {gen.isPending ? <><div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" /> Generating…</> : <><Sparkle size={14} weight="fill" className="mr-1" /> {summary ? "Regenerate" : "Generate"} Summary</>}
      </Button>
    </div>
  );
}


function ItemHeaderImage({ fileId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let objUrl = null;
    api.get(`/attachment-file/${fileId}`, { responseType: "blob" })
      .then((resp) => { if (!active) return; objUrl = URL.createObjectURL(resp.data); setUrl(objUrl); })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [fileId]);
  if (!url) return null;
  return <img src={url} alt="Item" className="h-12 w-12 rounded-sm object-cover border border-border" data-testid="item-header-image" />;
}
